import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { OrganizationOverviewComponent } from '../../Partials/organization-overview/organization-overview.component';
import { OrganizationEditComponent } from '../../Partials/organization-edit/organization-edit.component';
import { OrganizationNotesComponent } from '../../Partials/organization-notes/organization-notes.component';
import { OrganizationSubscriptionComponent } from '../../Partials/organization-subscription/organization-subscription.component';
import { OrganizationLicencesComponent } from '../../Partials/organization-licences/organization-licences.component';
import { OrganizationDevicesComponent } from '../../Partials/organization-devices/organization-devices.component';
import { OrganizationInvoicesComponent } from '../../Partials/organization-invoices/organization-invoices.component';
import { OrganizationPaymentsComponent } from '../../Partials/organization-payments/organization-payments.component';
import { AvEmptyStateComponent } from '../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvStatusChipComponent } from '../../../../Shared/Components/av-status-chip/av-status-chip.component';
import { OrganizationDetailFacade } from '../../../../../Facades/Organizations/organization-detail.facade';
import { OrganizationBillingFacade } from '../../../../../Facades/Organizations/organization-billing.facade';
import { OrganizationLicenceFacade } from '../../../../../Facades/Organizations/organization-licence.facade';
import { OrganizationDeviceFacade } from '../../../../../Facades/Organizations/organization-device.facade';
import { VALUE_UNRESOLVED } from '../../../../../Consts/backoffice.const';
import {
  ORGANIZATION_DEFAULT_TAB,
  ORGANIZATION_TABS,
  ORGANIZATION_TAB_QUERY_PARAM,
} from '../../../../../Consts/organization-detail.const';

@Component({
  selector: 'app-organization-detail',
  standalone: true,
  imports: [
    RouterLink,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    OrganizationOverviewComponent,
    OrganizationEditComponent,
    OrganizationNotesComponent,
    OrganizationSubscriptionComponent,
    OrganizationLicencesComponent,
    OrganizationDevicesComponent,
    OrganizationInvoicesComponent,
    OrganizationPaymentsComponent,
    AvEmptyStateComponent,
    AvStatusChipComponent,
  ],
  templateUrl: './organization-detail.component.html',
  styleUrl: './organization-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class OrganizationDetailComponent {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);

  readonly facade = inject(OrganizationDetailFacade);
  readonly billingFacade = inject(OrganizationBillingFacade);
  readonly licenceFacade = inject(OrganizationLicenceFacade);
  readonly deviceFacade = inject(OrganizationDeviceFacade);

  readonly unresolved = VALUE_UNRESOLVED;
  readonly tabs = ORGANIZATION_TABS;

  private readonly _params = toSignal(this._route.paramMap);
  private readonly _query = toSignal(this._route.queryParamMap);

  readonly activeTab = computed<string>(
    () => this._query()?.get(ORGANIZATION_TAB_QUERY_PARAM) || ORGANIZATION_DEFAULT_TAB,
  );

  constructor() {
    this._registerOrganizationParam();
  }

  selectTab(tabId: string): void {
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { [ORGANIZATION_TAB_QUERY_PARAM]: tabId },
      queryParamsHandling: 'merge',
    });
  }

  reload(): void {
    this.facade.reload();
    this.billingFacade.reload();
    this.licenceFacade.reload();
    this.deviceFacade.reload();
  }

  private _registerOrganizationParam(): void {
    effect(() => {
      const organizationId = this._params()?.get('id') ?? '';

      this._loadOrganization(organizationId);
    });
  }

  private _loadOrganization(organizationId: string): void {
    this.facade.load(organizationId);
    this.billingFacade.load(organizationId);
    this.licenceFacade.load(organizationId);
    this.deviceFacade.load(organizationId);
  }
}
